/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, versionGte } from '@msdyn365-commerce/core';
import { IQueryResultSettings, ProductRefiner, ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy';

/**
 * This setting defines inventory filtering options.
 */
export enum ProductListInventoryFilteringOptions {
    /**
     * Filter out all products out of stock.
     */
    HideOOS = 'hideOOS',

    /**
     * Sort products by availability, OOS goes last.
     */
    SortOOS = 'sortOOS',

    /**
     * No filtering selected.
     */
    Default = 'default'
}

/**
 * Input class for get inventory in stock refiner value action.
 */
export class InventoryInStockRefinerValueInput implements IActionInput {
    public actionContext: IActionContext;
    public channelId: number;
    public channelInventoryConfiguration;
    public catalogId?: number;

    public constructor(actionContext: IActionContext, channelId: number, channelInventoryConfiguration, catalogId: number = 0) {
        this.catalogId = catalogId;
        this.channelId = channelId;
        this.actionContext = actionContext;
        this.channelInventoryConfiguration = channelInventoryConfiguration;
    }

    public getCacheKey = (): string =>
        `${this.channelId}-${this.catalogId}-${this.channelInventoryConfiguration?.InventoryProductAttributeRecordId}`;
    public getCacheObjectType = (): string => 'RefinerList';
    public dataCacheType = (): CacheType => 'application';
}

/**
 * Uses Bulk refiner API to get refiners and child refiner values.
 * @param searchCriteria Product search criteria.
 * @param maxItems Max items.
 * @param queryResultSettings
 * @param ctx Action context.
 */
async function getProductRefinerHierarchy(
    searchCriteria: ProductSearchCriteria,
    queryResultSettings: IQueryResultSettings,
    ctx: IActionContext
): Promise<IProductRefinerHierarchy[]> {
    const rsVersion =
        !process.env.MSDyn365Commerce_RSVERSION || process.env.MSDyn365Commerce_RSVERSION === '--'
            ? '0.0'
            : process.env.MSDyn365Commerce_RSVERSION;
    const isHideAndSortByInventoryAvailabilitySupported = versionGte(rsVersion, '9.37');
    if (isHideAndSortByInventoryAvailabilitySupported) {
        // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
        const productsDataActions = require('@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g');
        return productsDataActions
            .getProductSearchRefinersAsync({ callerContext: ctx, queryResultSettings }, searchCriteria)
            .then((productRefiners: ProductRefiner[]) => {
                if (!productRefiners) {
                    ctx.trace('[getRefinersByCriteriaAction] returned no refiners');
                    return <IProductRefinerHierarchy[]>[];
                }

                const result = productRefiners as IProductRefinerHierarchy[];

                if (result.length === 0) {
                    ctx.trace('[getRefinersByCriteriaAction] returned no IProductRefinerHierarchy objects');
                    return <IProductRefinerHierarchy[]>[];
                }

                return result;
            });
    } else {
        return Promise.resolve([]);
    }
}

/**
 * Action method returns inventory in stock refiners.
 * @param input - InventoryInStockRefinerValueInput.
 * @param context - IActionContext.
 * @returns - Promise<IProductRefinerHierarchy | undefined>.
 */
export async function getInventoryInStockRefiner(
    input: InventoryInStockRefinerValueInput,
    context: IActionContext
): Promise<IProductRefinerHierarchy | undefined> {
    const queryResultSettings = {
        count: true
    };

    const refiners = await getProductRefinerHierarchy(
        {
            Context: {
                ChannelId: input.channelId,
                CatalogId: input.catalogId
            }
        },
        queryResultSettings,
        context
    );

    // InventoryProductAttributeRecordId may be 0, and recId of default refiner Category/Price/Rating is always 0 by design. In this case, we should not return refiner
    // Why recId of default refiner Category/Price/Rating is 0: see GetChannelProductRefiners.sql
    const inventoryRefiner =
        input.channelInventoryConfiguration?.InventoryProductAttributeRecordId !== 0 &&
        refiners.find(refiner => refiner.RecordId === input.channelInventoryConfiguration?.InventoryProductAttributeRecordId);
    let inStockRefiner;
    if (inventoryRefiner) {
        inStockRefiner = inventoryRefiner.Values.find(
            value =>
                value.LeftValueBoundString !== input.channelInventoryConfiguration.InventoryOutOfStockAttributeValueText &&
                value.RightValueBoundString !== input.channelInventoryConfiguration.InventoryOutOfStockAttributeValueText
        );
    }

    return inStockRefiner;
}

/**
 * Action.
 * @param id - Id.
 * @param action - Action.
 * @returns - Results.
 */
export const getInventoryInStockRefinerAction = createObservableDataAction({
    id: '@msdyn365-commerce/get-inventory-in-stock-refiner',
    action: getInventoryInStockRefiner as IAction<IProductRefinerHierarchy>
});

export default getInventoryInStockRefinerAction;
